/**
 * A modal dialog, built on the native `<dialog>` element.
 *
 * Using the platform's `showModal()` rather than a hand-rolled overlay is the
 * whole decision, and it buys four things that div-based modals routinely get
 * wrong: focus is trapped inside, everything behind it becomes inert to both
 * pointer and assistive technology, Escape closes it, and it renders in the
 * top layer so no `z-index` in the application can cover it.
 *
 * The residual work is small: React must not render the children while closed
 * (a form inside a hidden dialog still submits), and the browser's own Escape
 * has to be routed back into React state.
 */

import { useEffect, useRef, type ReactNode } from "react";

import "./layout.css";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Buttons. The confirming one should be focused by the caller. */
  footer?: ReactNode;
  children?: ReactNode;
  closeLabel: string;
  /**
   * `side` slides in from the inline end at full height — a panel of
   * settings beside the page rather than a question in front of it. Same
   * element, same focus trap: only the geometry differs.
   */
  placement?: "center" | "side";
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  footer,
  children,
  closeLabel,
  placement = "center",
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (open && !element.open) {
      // `showModal`, not `show`: only the modal form traps focus and makes the
      // rest of the page inert.
      element.showModal();
    } else if (!open && element.open) {
      element.close();
    }
  }, [open]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // The browser closes on Escape without telling React, which would leave
    // `open` true and the dialog unopenable until it was toggled twice.
    const handleClose = () => {
      onClose();
    };
    element.addEventListener("close", handleClose);
    return () => {
      element.removeEventListener("close", handleClose);
    };
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      className={placement === "side" ? "hardauto-dialog hardauto-dialog--side" : "hardauto-dialog"}
      aria-labelledby="hardauto-dialog-title"
    >
      {/*
        Children are unmounted while closed. A `<dialog>` that is merely hidden
        still contains live form controls, which still submit and still receive
        programmatic focus.
      */}
      {open ? (
        <div className="hardauto-dialog__inner">
          <header className="hardauto-dialog__header">
            <h2 id="hardauto-dialog-title" className="hardauto-dialog__title">
              {title}
            </h2>
            <button
              type="button"
              className="hardauto-dialog__close"
              onClick={onClose}
              aria-label={closeLabel}
            >
              <span aria-hidden="true">×</span>
            </button>
          </header>
          {description ? <p className="hardauto-dialog__description">{description}</p> : null}
          {children ? <div className="hardauto-dialog__body">{children}</div> : null}
          {footer ? <footer className="hardauto-dialog__footer">{footer}</footer> : null}
        </div>
      ) : null}
    </dialog>
  );
}
